'use client';

/**
 * CollectionsSection — collection list for rail cards.
 *
 * Mode4-native parallel of `<CollectionsSection />` under
 * copilot/details/sections/.
 *
 * Thin row list (name + product count). Click selects the
 * collection node so the rail can re-render.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { Grid2X2 } from 'lucide-react';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';

interface CollectionLike {
  semantic_id?: string;
  name?: string;
  product_count?: number;
}

interface CollectionsSectionProps {
  collections: CollectionLike[] | null | undefined;
  label?: string | null;
  maxItems?: number | null;
}

export function CollectionsSection({ collections, label, maxItems }: CollectionsSectionProps) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);  if (!collections || collections.length === 0) return null;

  const display = maxItems ? collections.slice(0, maxItems) : collections;
  const overflow = collections.length - display.length;

  return (
    <div>
      <p className={text.eyebrow}>
        {label || 'Collections'}{' '}
        <span className="text-muted-foreground">({collections.length})</span>
      </p>
      <ul className="space-y-0.5">
        {display.map((c) => (
          <li key={c.semantic_id ?? c.name}>
            <button
              onClick={() =>
                c.semantic_id &&
                selectNode({
                  id: c.semantic_id,
                  label: c.name ?? 'Collection',
                  type: 'collection',
                  properties: { product_count: c.product_count },
                })
              }
              className={cn(
                'w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-accent/50 transition-colors text-left',
                text.body,
              )}
            >
              <Grid2X2 className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0 truncate">{c.name ?? 'Collection'}</span>
              <span className={cn(text.muted, 'shrink-0')}>
                {c.product_count ?? '—'}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className={cn(text.muted, 'mt-1')}>+{overflow} more</p>
      )}
    </div>
  );
}
